/**
 * Exact decimal arithmetic on decimal strings.
 *
 * JavaScript numbers are IEEE-754 doubles, and `0.1 + 0.2 === 0.30000000000000004`. That is
 * tolerable for a chart and intolerable for an invoice: the line editor's running total has to
 * match, to the paisa, the total the server will compute in C# `decimal` and store in
 * `numeric(19,4)`. A total that changes when you press Save is the defect this file prevents.
 *
 * Values are held as a `bigint` of scaled units plus the scale, which is exact for anything the
 * BIS chain does — add, subtract, multiply, divide by a quantity, round once at a defined point.
 * No library: the operations needed are these six, and a dependency here would be a licence, a
 * bundle and a supply chain for something a page of code covers.
 */
export interface Decimal {
  readonly units: bigint;
  readonly scale: number;
}

/** Working scale for intermediates. Prices carry unrestricted decimals; this is generous. */
const WORKING_SCALE = 12;

const POWERS: bigint[] = Array.from({ length: 32 }, (_, i) => 10n ** BigInt(i));

function pow10(n: number): bigint {
  return POWERS[n] ?? 10n ** BigInt(n);
}

/**
 * Parses a decimal string. Anything unparseable is zero rather than NaN — a total that reads
 * "NaN" is useless, and a half-typed "12." in a line editor is a normal keystroke, not an error.
 */
export function decimal(value: string | number | null | undefined): Decimal {
  if (value === null || value === undefined || value === "")
    return { units: 0n, scale: WORKING_SCALE };

  const text = String(value).trim();
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!match) return { units: 0n, scale: WORKING_SCALE };

  const [, sign, whole = "", fraction = ""] = match;
  const digits = `${whole || "0"}${fraction.padEnd(WORKING_SCALE, "0").slice(0, WORKING_SCALE)}`;
  const units = BigInt(digits || "0");

  return { units: sign === "-" ? -units : units, scale: WORKING_SCALE };
}

export const ZERO: Decimal = { units: 0n, scale: WORKING_SCALE };

function align(a: Decimal, b: Decimal): [bigint, bigint, number] {
  if (a.scale === b.scale) return [a.units, b.units, a.scale];
  const scale = Math.max(a.scale, b.scale);
  return [a.units * pow10(scale - a.scale), b.units * pow10(scale - b.scale), scale];
}

export function add(a: Decimal, b: Decimal): Decimal {
  const [x, y, scale] = align(a, b);
  return { units: x + y, scale };
}

export function subtract(a: Decimal, b: Decimal): Decimal {
  const [x, y, scale] = align(a, b);
  return { units: x - y, scale };
}

export function multiply(a: Decimal, b: Decimal): Decimal {
  // The scales add, then the result is brought back to the working scale so intermediates do not
  // grow without bound down a long invoice.
  const raw = a.units * b.units;
  const rawScale = a.scale + b.scale;
  return rescale({ units: raw, scale: rawScale }, WORKING_SCALE);
}

/** Division by zero is refused rather than producing Infinity — the caller must decide. */
export function divide(a: Decimal, b: Decimal): Decimal | null {
  if (b.units === 0n) return null;

  // Scale up first so the quotient keeps the working scale's worth of digits.
  const [x, y] = align(a, b);
  return { units: (x * pow10(WORKING_SCALE)) / y, scale: WORKING_SCALE };
}

function rescale(value: Decimal, scale: number): Decimal {
  if (value.scale === scale) return value;
  if (value.scale < scale) return { units: value.units * pow10(scale - value.scale), scale };

  // Truncation, not rounding: this is an intermediate, and rounding happens once at the points
  // EN 16931 defines (ADR-0008). Rounding here would round twice.
  return { units: value.units / pow10(value.scale - scale), scale };
}

/**
 * Rounds to `places`, half **away from zero** — the mode the Nepal pack sets, and the one the
 * legacy system's `.ToString("n2")` happened to produce. Not banker's rounding: `2.005` is `2.01`,
 * and `-2.005` is `-2.01`.
 */
export function round(value: Decimal, places = 2): Decimal {
  if (value.scale <= places) return rescale(value, places);

  const divisor = pow10(value.scale - places);
  const quotient = value.units / divisor;
  const remainder = value.units % divisor;
  const half = divisor / 2n;

  const away = remainder >= half ? 1n : remainder <= -half ? -1n : 0n;

  return { units: quotient + away, scale: places };
}

/** A decimal string with exactly `places` decimals — the wire format. */
export function toFixed(value: Decimal, places = 2): string {
  const rounded = round(value, places);
  const negative = rounded.units < 0n;
  const digits = (negative ? -rounded.units : rounded.units).toString().padStart(places + 1, "0");
  const whole = digits.slice(0, digits.length - places) || "0";
  const fraction = places > 0 ? `.${digits.slice(digits.length - places)}` : "";
  return `${negative ? "-" : ""}${whole}${fraction}`;
}

export function isZero(value: Decimal): boolean {
  return value.units === 0n;
}

/** Compares two decimals; the sign of the result is the usual comparator convention. */
export function compare(a: Decimal, b: Decimal): number {
  const [x, y] = align(a, b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/** For display and charts only. Never feed the result back into a calculation. */
export function toNumber(value: Decimal): number {
  return Number(toFixed(value, 6));
}
